import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useNonInitialEffect from '@versiondos/hooks';
import { getInvoiceThunk } from '@spike/invoices-action';
import clsx from 'clsx';
import { Button, PriceField2, Spinner, DescriptionField2 } from 'components/UI';
import Invoice from '@spike/invoice-model';
import { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { InvoicesStatus } from '@spike/invoices-action';
import { RootState } from 'store';
import { Items } from './Items';
import { Payment } from '@spike/payments-model';
import { getPaymentThunk, refundThunk } from '@spike/payments-action';
import { PaymentsStatus } from '@spike/payments-action';
import { FieldError } from '@spike/model';
import { useApiClientWrapper } from 'hooks';
import styles from './Refund.module.scss';

interface RefundProps {
    paymentId: number;
    className?: string;
    onClose?: () => void;
    onRefunded?: () => void;
}

export const Refund: FunctionComponent<RefundProps> = props => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();

    const invoice = useSelector<RootState, Invoice | undefined>(state => state.invoices.invoice);
    const invoicesStatus = useSelector<RootState, InvoicesStatus>(state => state.invoices.status);

    const payment = useSelector<RootState, Payment | undefined>(state => state.payments.payment);
    const paymentsStatus = useSelector<RootState, PaymentsStatus>(state => state.payments.status);

    const [loading, setLoading] = useState(true);
    const [refunding, setRefunding] = useState(false);
    const [reason, setReason] = useState('');
    const [refundAmount, setRefundAmount] = useState(0);
    const [errors, setErrors] = useState<Array<FieldError>>([]);

    const loadedPayment = payment?.id === props.paymentId ? payment : undefined;

    useEffect(() => {
        setLoading(true);
        dispatch(getPaymentThunk(apiClientWrapper, props.paymentId));
    }, [props.paymentId]);

    useNonInitialEffect(() => {
        if (PaymentsStatus.GetPaymentSuccess === paymentsStatus && loadedPayment) {
            dispatch(getInvoiceThunk(apiClientWrapper, loadedPayment.invoice.id));
        } else if (PaymentsStatus.RefundSuccess === paymentsStatus) {
            setRefunding(false);
            props.onRefunded?.();
        } else if (PaymentsStatus.Error === paymentsStatus) {
            setRefunding(false);
        }
    }, [paymentsStatus]);

    useNonInitialEffect(() => {
        if (InvoicesStatus.GetInvoiceSuccess === invoicesStatus && loadedPayment) {
            setLoading(false);
        } else if (InvoicesStatus.Error === invoicesStatus) {
            setLoading(false);
        }
    }, [invoicesStatus]);

    const validate = (amount: number, paymentToValidate: Payment): Array<FieldError> => {
        const errors: Array<FieldError> = [];

        if (amount <= 0) {
            errors.push({
                fieldName: 'amount',
                errorMessage: 'Amount must be greater than 0.00'
            });
        }

        if (amount > 0 && amount > Number(paymentToValidate.balance)) {
            errors.push({
                fieldName: 'amount',
                errorMessage: `Amount cannot be greater than $${paymentToValidate.balance}`
            });
        }

        return errors;
    };

    const refundHandler = () => {
        if (!loadedPayment) {
            return;
        }

        const errors = validate(refundAmount, loadedPayment);

        if (errors.length === 0) {
            setRefunding(true);
            dispatch(refundThunk(apiClientWrapper, loadedPayment.id, refundAmount, reason));
        }

        setErrors(errors);
    };

    const amountError = errors.find(error => error.fieldName === 'amount');

    const showContent = !loading && loadedPayment && invoice;

    return (
        <div className={clsx(styles.container, props.className)}>
            <div className={styles.header}>
                <div className={styles.leftHeader}></div>
                <div className={styles.centerHeader}>
                    <h2 className={styles.headerTitle}>Refund</h2>
                </div>
                <div className={styles.rightHeader}>
                    <FontAwesomeIcon
                        className={styles.closeIcon}
                        icon={faTimes}
                        onClick={() => props.onClose && props.onClose()}
                    />
                </div>
            </div>
            <div className={styles.body}>
                {!showContent && <Spinner className={styles.spinner} />}

                {showContent && (
                    <>
                        <div className={styles.bodyHeader}>
                            <div className={styles.bodyHeaderLeft}>
                                <p className={styles.name}>{invoice.customer?.name}</p>
                                <p className={styles.email}>{invoice.customer?.email}</p>
                            </div>
                            <div className={styles.bodyHeaderRight}>
                                <p className={styles.title}>INVOICE DATE</p>
                                <p className={styles.date}>{invoice.invoiceDate.format('ll - hh:mm a')}</p>
                            </div>
                        </div>
                        <Items payment={loadedPayment} />
                    </>
                )}
            </div>

            <div className={styles.footer}>
                {showContent && (
                    <>
                        <div className={styles.footerRow}>
                            <DescriptionField2
                                label="Reason (optional)"
                                value={reason}
                                onChange={value => {
                                    setReason(value);
                                }}
                                maxRows={3}
                                className={styles.reason}
                            />
                        </div>
                        <div className={styles.amountSection}>
                            <div className={styles.amountRow}>
                                <div className={clsx(styles.amountField, amountError && styles.amountFieldInvalid)}>
                                    <PriceField2
                                        label="Amount:"
                                        name="amount"
                                        value={0}
                                        onChange={value => setRefundAmount(value || 0)}
                                        precision={6}
                                        scale={2}
                                    />
                                </div>
                                <Button
                                    label="Refund"
                                    loading={refunding}
                                    disabled={refundAmount <= 0}
                                    className={styles.button}
                                    onClick={refundHandler}
                                />
                            </div>
                            <div
                                className={styles.amountError}
                                role={amountError ? 'alert' : undefined}
                            >
                                {amountError?.errorMessage}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default Refund;
